import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Check } from '@material-ui/icons';
import { lightTheme as theme } from '../../theme';

const StepperContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 32px;
    position: relative;
    padding: 0 16px;
`;

// The connecting line behind steps
const ConnectorLine = styled.div`
    position: absolute;
    top: 14px; /* Half of StepCircle height (28px/2) approximately */
    left: 40px;
    right: 40px;
    height: 2px;
    background: ${theme.colors.gray200};
    z-index: 0;
`;

// Individual Step Wrapper
const StepWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
    background: ${theme.colors.paper || '#F8FAFC'}; /* Match page bg to hide connector */
    padding: 0 8px; 
`;

const StepCircle = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 700;
    transition: all 0.3s ease;

    ${({ state }) => {
        switch (state) {
            case 'completed':
                return css`
                    background: ${theme.colors.success};
                    color: white;
                    border: 2px solid ${theme.colors.success};
                `;
            case 'active':
                return css`
                    background: ${theme.colors.action};
                    color: white;
                    border: 2px solid ${theme.colors.action};
                    box-shadow: 0 0 0 4px rgba(6, 182, 212, 0.2); /* Soft Glow */
                `;
            case 'pending':
            default:
                return css`
                    background: transparent;
                    color: ${theme.colors.gray400};
                    border: 2px solid ${theme.colors.gray300};
                `;
        }
    }}
`;

const StepLabel = styled.span`
    font-size: 12px;
    font-weight: ${({ active }) => (active ? '700' : '500')};
    color: ${({ active, completed }) =>
        active ? theme.colors.primary :
            completed ? theme.colors.success :
                theme.colors.gray500};
    text-align: center;
    white-space: nowrap;
`;

// Helper to determine step state
const getStepState = (index, currentStep) => {
    if (index < currentStep) return 'completed';
    if (index === currentStep) return 'active';
    return 'pending';
};

const Stepper = ({ steps, currentStep }) => {
    return (
        <StepperContainer>
            <ConnectorLine />
            {steps.map((label, index) => {
                const state = getStepState(index, currentStep);
                return (
                    <StepWrapper key={index}>
                        <StepCircle state={state}>
                            {state === 'completed' ? <Check style={{ fontSize: 18 }} /> : index + 1}
                        </StepCircle>
                        <StepLabel
                            active={state === 'active'}
                            completed={state === 'completed'}
                        >
                            {label}
                        </StepLabel>
                    </StepWrapper>
                );
            })}
        </StepperContainer>
    );
};

Stepper.propTypes = {
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentStep: PropTypes.number.isRequired,
};

export default Stepper;
